import { Component, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { redirectIfNotAdmin, ucFirst } from 'src/app/globals';
import {
  DaypartException,
  Daypart,
  DaypartObj,
  GenericWarningDialogData,
  DataStoreExport,
} from 'src/app/interfaces';
import { EditDaypartComponent } from './dialogs/edit-daypart/edit-daypart.component';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { collection, deleteDoc, doc, onSnapshot, query, where } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import moment from 'moment';
import { ManageExceptionComponent } from './dialogs/manage-exception/manage-exception.component';
import { MatIcon } from '@angular/material/icon';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { NgTemplateOutlet } from '@angular/common';
import { MatFabButton, MatIconButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-day-parts',
  templateUrl: './day-parts.component.html',
  styleUrls: ['./day-parts.component.scss'],
  imports: [
    MatIcon,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    MatFabButton,
    MatCard,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatIconButton,
    MatProgressSpinner,
  ],
})
export class DayPartsComponent implements OnInit {
  loadingPage = signal(true);
  loading = signal(true);
  dataStore: DataStoreExport = dataStore;
  organisationId = this.dataStore.selectedOrganisation.id;
  dayparts = signal<any[]>([]);
  exceptions = signal<DaypartException[]>([]);
  repeatValues = {
    never: 'Nooit',
    year: 'Jaarlijks',
    month: 'Maandelijks',
    week: 'Wekelijks',
  };
  type = signal('driver');
  usesPlannerPlanning = signal(false);

  moment = moment;
  ucFirst = ucFirst;

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  async ngOnInit() {
    if (dataStore?.generalSettings?.usesPlannerPlanning) {
      this.usesPlannerPlanning.set(true);
    }

    await this.getDayparts();
    await this.getExceptions();
    redirectIfNotAdmin(this.router);
    this.loadingPage.set(false);
  }

  async changeTab(e) {
    if (e.index === 0) {
      this.type.set('driver');
      await this.getDayparts();
    } else if (e.index === 1) {
      this.type.set('planner');
      await this.getDayparts();
    } else if (e.index === 2) {
      this.type.set('exceptions');
    }
  }

  addDayPart() {
    this.dialog.open(EditDaypartComponent, {
      width: '475px',
      data: { type: this.type() },
    });
  }

  editDaypart(daypart: DaypartObj, id: string) {
    this.dialog.open(EditDaypartComponent, {
      width: '475px',
      data: { daypart: { ...daypart }, id, type: this.type() },
    });
  }

  manageException(exception?: DaypartException) {
    this.dialog.open(ManageExceptionComponent, {
      width: '550px',
      data: { exception: exception ? { ...exception } : null },
    });
  }

  deleteException(exception: DaypartException) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteException',
        optionalData: {
          exceptionName: exception.title,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await deleteDoc(
          doc(db, `organisations/${this.organisationId}/dayPartExceptions/${exception.id}`),
        );
        this.snackBar.open('Uitzondering is verwijderd.', 'X', {
          duration: 5000,
        });
      }
    });
  }

  async removeDaypart(daypart) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteDayparts',
        optionalData: {
          daypartName: daypart.name,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await deleteDoc(doc(db, `organisations/${this.organisationId}/dayparts/${daypart.id}`));
        this.snackBar.open('Dagdeel is verwijderd.', 'X', {
          duration: 5000,
        });
      }
    });
  }

  async getDayparts() {
    this.loading.set(true);
    const q = query(
      collection(db, `organisations/${this.organisationId}/dayparts`),
      where('type', '==', this.type()),
    );
    onSnapshot(q, (snapshot) => {
      const dayparts: any[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as Daypart;
        data.id = doc.id;
        const daypart = this.createDaypartObject(data);
        dayparts.push(daypart);
      });
      dayparts.sort((a, b) => {
        return (
          a.day - b.day ||
          Number(a.startHour) - Number(b.startHour) ||
          Number(a.startMin) - Number(b.startMin) ||
          Number(a.endHour) - Number(b.endHour) ||
          Number(a.endMin) - Number(b.endMin)
        );
      });
      this.dayparts.set(dayparts);
      this.loading.set(false);
    });
  }

  async getExceptions() {
    this.loading.set(true);
    onSnapshot(
      collection(db, `organisations/${this.organisationId}/dayPartExceptions`),
      (snapshot) => {
        const exceptions: DaypartException[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as DaypartException;
          data.id = doc.id;
          data.repeatValue = data.repeatValue ?? 'never';
          exceptions.push(data);
        });
        exceptions.sort((a, b) => {
          return (
            new Date(a.date.toDate()).getTime() - new Date(b.date.toDate()).getTime() ||
            Number(a.startHour) - Number(b.startHour) ||
            Number(a.startMinutes) - Number(b.startMinutes) ||
            Number(a.endHour) - Number(b.endHour) ||
            Number(a.endMinutes) - Number(b.endMinutes)
          );
        });
        this.exceptions.set(exceptions);
        this.loading.set(false);
      },
    );
  }

  createDaypartObject(data: Daypart): DaypartObj {
    const daypartObj = {} as DaypartObj;
    daypartObj.id = data.id;
    daypartObj.day = data.daypart.day;
    daypartObj.name = data.daypart.name;
    daypartObj.vehicles = data.vehicles ?? [];
    daypartObj.dayString = this.getDaypartString(data.daypart.day);
    daypartObj.startHour = this.fixHourString(data.daypart.startHour.toString());
    daypartObj.endHour = this.fixHourString(data.daypart.endHour.toString());
    daypartObj.startMin = this.fixMinuteString(data.daypart.startMin.toString());
    daypartObj.endMin = this.fixMinuteString(data.daypart.endMin.toString());
    return daypartObj;
  }

  fixHourString(hour: string): string {
    return hour.length !== 1 ? hour : `0${hour}`;
  }

  fixMinuteString(minute: string): string {
    if (minute.length > 1) {
      return minute;
    }
    return minute === '5' ? `0${minute}` : `${minute}0`;
  }

  getDaypartString(number: number): string {
    switch (number) {
      case 0:
        return 'Maandag';
      case 1:
        return 'Dinsdag';
      case 2:
        return 'Woensdag';
      case 3:
        return 'Donderdag';
      case 4:
        return 'Vrijdag';
      case 5:
        return 'Zaterdag';
      case 6:
        return 'Zondag';
    }
  }

  getTimeString(data) {
    const start = moment().set({
      hour: data.startHour,
      minutes: data.startMinutes,
    });
    const end = moment().set({
      hour: data.endHour,
      minutes: data.endMinutes,
    });
    return start.format('HH:mm').concat(' - ', end.format('HH:mm'));
  }
}
